import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, TriangleAlertIcon } from 'lucide-react';
import { Panel } from '../../components/ui/Panel';
import { PanelHeader } from '../../components/ui/PanelHeader';
import { Button } from '../../components/ui/Button';
import { MetricStat } from '../../components/admin/MetricStat';
import { AccountStateBadge } from '../../components/admin/AccountStateBadge';
import { customerAccounts } from '../../data/adminCustomers';
import { fleetChecks, platformIncidents, workerRegions } from '../../data/adminPlatform';
import { relativeTime } from '../../utils/format';
export function AdminOverview() {
  const open = platformIncidents.find(incident => !incident.resolvedAt);
  const mrr = customerAccounts.reduce((sum, c) => sum + c.mrr, 0);
  const active = customerAccounts.filter(c => c.state === 'active').length;
  const pastDue = customerAccounts.filter(c => c.state === 'past_due');
  const flagged = customerAccounts.filter(c => c.flag);
  const failing = fleetChecks.filter(check => check.state === 'failing');
  const services = customerAccounts.reduce((sum, c) => sum + c.servicesUsed, 0);
  return <div className="space-y-5" data-id="element-599" data-mp="3xt8d4fouan5">
      <header data-id="element-600" data-mp="8ujlpk03ys4v">
        <h1 className="text-xl font-semibold tracking-tight text-ink" data-id="element-601" data-mp="8u1gvyzdr5lc">
          Platform overview
        </h1>
        <p className="mt-1 text-sm text-ink-muted" data-id="element-602" data-mp="duxpgpuoffx3">
          What needs a person today, then the numbers behind it.
        </p>
      </header>

      {open && <section className="overflow-hidden rounded-card border border-warn-border bg-warn-soft shadow-card" aria-labelledby="open-incident" data-id="element-603" data-mp="muca38o5ww9o">
          <div className="flex flex-wrap items-start gap-4 p-5" data-id="element-604" data-mp="rnx2ml6zky1h">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-warn text-white" data-id="element-605" data-mp="wk48yf3aobjy">
              <TriangleAlertIcon className="h-4 w-4" aria-hidden="true" data-id="element-606" data-mp="11ll55xau39u7" />
            </span>
            <div className="min-w-0 flex-1" data-id="element-607" data-mp="15lllxolenx4f">
              <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-warn-ink/80" data-id="element-608" data-mp="32lq1vlmyyoua">
                Open incident · started {relativeTime(open.startedAt)}
              </p>
              <h2 id="open-incident" className="mt-1.5 text-base font-semibold text-warn-ink" data-id="element-609" data-mp="3be9wmb05499u">
                {open.title}
              </h2>
              <p className="mt-2 max-w-2xl text-[13px] leading-relaxed text-warn-ink/90" data-id="element-610" data-mp="36lqhh9lt162x">
                {open.body}
              </p>
              <p className="mt-3 font-mono text-xs text-warn-ink/80" data-id="element-611" data-mp="3ge60ma8u80pe">
                Affects {open.affected}
              </p>
              <div className="mt-4 flex flex-wrap gap-2" data-id="element-612" data-mp="3ucm6jyyvln6h">
                <Button size="sm" to="/admin/system" data-id="element-613" data-mp="3qb0ycqxadx2m">
                  Open system health
                </Button>
                <Button variant="secondary" size="sm" to="/admin/fleet" data-id="element-614" data-mp="1zycxxv2i2azf">
                  See affected checks
                </Button>
              </div>
            </div>
          </div>
        </section>}

      <Panel data-id="element-615" data-mp="1luw17oq3s1b9">
        <div className="grid divide-y divide-line sm:grid-cols-2 sm:divide-y-0 lg:grid-cols-4 lg:divide-x" data-id="element-616" data-mp="1lu4d0yyu55v0">
          <MetricStat label="MRR" value={`$${mrr}`} detail={`${active} active accounts`} emphasis data-id="element-617" data-mp="1pueo2q1himgq" />
          <MetricStat label="Services monitored" value={String(services)} detail="Across all plans" data-id="element-618" data-mp="1h22vaxiwemxl" />
          <MetricStat label="Failing checks" value={String(failing.length)} detail="Right now, across the fleet" tone={failing.length > 0 ? 'critical' : 'default'} data-id="element-619" data-mp="127pzxmlvtxro" />
          <MetricStat label="Past due" value={String(pastDue.length)} detail={`$${pastDue.reduce((s, c) => s + c.mrr, 0)} at risk`} tone={pastDue.length > 0 ? 'warn' : 'default'} data-id="element-620" data-mp="y3qlrao2rb1h" />
        </div>
      </Panel>

      <div className="grid gap-5 lg:grid-cols-[1.35fr_1fr]" data-id="element-621" data-mp="2sh9n6vl87ds2">
        <Panel as="section" data-id="element-622" data-mp="seqnllh5r63y">
          <PanelHeader title="Accounts needing attention" description="Billing failures, quota ceilings and trials about to lapse." level={2} action={<Button variant="ghost" size="sm" to="/admin/customers" data-id="element-624" data-mp="ifssfzp88ebz">
                All customers
                <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-625" data-mp="efqw8629gd43" />
              </Button>} data-id="element-623" data-mp="sf0ercdxvjb8" />
          <ul className="divide-y divide-line" data-id="element-626" data-mp="4q7g385xmxcx">
            {flagged.map(customer => <li key={customer.id} data-id="element-627" data-mp="4oe5jhs77szn">
                <Link to={`/admin/customers/${customer.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3.5 transition-colors duration-150 ease-snap hover:bg-canvas focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand" data-id="element-628" data-mp="3vvqr09046sld">
                  <span className="min-w-0 flex-1" data-id="element-629" data-mp="3hyu1qbzn9j6l">
                    <span className="flex items-center gap-2" data-id="element-630" data-mp="1xfhsp8sh5234">
                      <span className="truncate text-[13px] font-medium text-ink" data-id="element-631" data-mp="22drqlr5l2gsf">
                        {customer.company}
                      </span>
                      <AccountStateBadge state={customer.state} data-id="element-632" data-mp="26godvv62j12p" />
                    </span>
                    <span className="mt-1 block truncate text-xs text-ink-muted" data-id="element-633" data-mp="2bdeq27er5ciu">
                      {customer.flag}
                    </span>
                  </span>
                  <span className="font-mono text-[13px] text-ink-muted" data-id="element-634" data-mp="1ipaljp7x35hq">
                    ${customer.mrr}/mo
                  </span>
                </Link>
              </li>)}
          </ul>
        </Panel>

        <Panel as="section" data-id="element-635" data-mp="1sgncmelw5rmp">
          <PanelHeader title="Check workers" description="Queue depth is the number that matters." level={2} data-id="element-636" data-mp="1sgwv7h4lwwid" />
          <ul className="divide-y divide-line" data-id="element-637" data-mp="zyj5k4tfcgud">
            {workerRegions.map(region => <li key={region.id} className="px-5 py-3.5" data-id="element-638" data-mp="239f2y62hrcuk">
                <div className="flex items-center justify-between gap-3" data-id="element-639" data-mp="237vmaecxazs9">
                  <span className="flex items-center gap-2" data-id="element-640" data-mp="1yalyf356mw6j">
                    <span className={`h-1.5 w-1.5 rounded-full ${region.state === 'ok' ? 'bg-healthy' : region.state === 'degraded' ? 'bg-warn' : 'bg-critical'}`} aria-hidden="true" data-id="element-641" data-mp="2c2s41vaplkrq" />
                    <span className="text-[13px] font-medium text-ink" data-id="element-642" data-mp="1tcapcumpoppd">
                      {region.name}
                    </span>
                    <span className="font-mono text-[11px] text-ink-soft" data-id="element-643" data-mp="1obmolh8okuhy">
                      {region.code}
                    </span>
                  </span>
                  <span className={`font-mono text-[13px] ${region.queueDepth > 100 ? 'text-warn-ink' : 'text-ink-muted'}`} data-id="element-644" data-mp="2zsw5px4c7ag3">
                    {region.queueDepth} queued
                  </span>
                </div>
                <p className="mt-1.5 font-mono text-[11px] text-ink-soft" data-id="element-645" data-mp="2yd1yqu5297uc">
                  {region.checksPerMinute.toLocaleString()} checks/min · p95{' '}
                  {region.p95Ms}ms
                </p>
              </li>)}
          </ul>
        </Panel>
      </div>
    </div>;
}